import {
    Course,
    CourseParticipation,
    CourseParticipationStatusResponse,
} from '@cna/projects/ak-jura/shared/interfaces';
import { UUID } from '@cna/shared/generic-types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LearningModuleDocumentInfo } from '../../../../../../../../libs/projects/ak-jura/shared/interfaces/src/lib/learning-module-document-info';
import { loadCourse } from '../customer/actions';
import { selectModuleInfo } from '../module_infos/selectors';
import * as globalActions from './actions';
import * as errodDescribtionActions from '../error_describtion/actions';
import {
    catchError,
    exhaustMap,
    map,
    mergeMap,
    switchMap, takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import {
    selectCourseParticipationId,
    selectModuleId,
} from '../router.selector';
import { of } from 'rxjs';
import { ToastType } from '../../../../../../../../libs/projects/ak-jura/shared/interfaces/src/lib/toast-type.enum';
import { ExamParticipation } from '../../../../../../../../libs/projects/ak-jura/shared/interfaces/src/lib/data-management/exam/exam-participation';
import { environment } from '../../../environments/environment';
import { Injectable } from '@angular/core';
import { EntityServices } from '@ngrx/data';
import { HttpClient } from '@angular/common/http';
import { createAction, Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import { ToasterService } from '../../../../../../../../libs/projects/ak-jura/angular/shared-components/src/lib/services/toaster.service';
import { selectCourse } from './selectors';

@Injectable()
export class CourseEffects {
    constructor(
        private actions$: Actions,
        private http: HttpClient,
        private entityServices: EntityServices,
        private store: Store,
        private router: Router,
        private route: ActivatedRoute,
        private toasterService: ToasterService
    ) {}

    /**
     * Set Course Participation in URL
     */
    setCourseParticipation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(globalActions.setCourseParticipation),
            withLatestFrom(this.store.select(selectCourseParticipationId)),
            mergeMap(
                ([{ courseParticipationId }, actualCourseParticipationId]) => {
                    if (
                        courseParticipationId &&
                        actualCourseParticipationId !== courseParticipationId
                    ) {
                        return this.router.navigate([courseParticipationId], {
                            replaceUrl: true,
                        });
                    } else {
                        return of(
                            globalActions.setCourseParticipationSkipped()
                        );
                    }
                }
            ),
            map(navResult => {
                if (typeof navResult === 'boolean') {
                    if (navResult) {
                        return globalActions.setCourseParticipationSuccess();
                    } else {
                        return globalActions.setCourseParticipationFailure();
                    }
                } else if (navResult === null) {
                    return globalActions.setCourseParticipationSkipped();
                } else {
                    return navResult;
                }
            })
        );
    });

    setCourseParticipationFailure$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(globalActions.setCourseParticipationFailure),
                tap(action => {
                    this.toasterService.show(
                        'Fehler beim setzen des Kurses!',
                        ToastType.error
                    );
                })
            );
        },
        { dispatch: false }
    );

    /**
     * save CourseExtension
     */

    saveCourseExtension$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(globalActions.saveCourseExtension),
            mergeMap(action => {
                return this.http.post<ExamParticipation>(
                    environment.apiHost + '/course-participation/extension',
                    {
                        courseParticipationId: action.courseParticipationId,
                        extensionMonthsNumber: action.extensionMonthsNumber,
                        invoiceAddress: action.invoiceAddress,
                        costPerMonth: action.costPerMonth,
                        vat: action.vat,
                    }
                );
            }),
            map((a: any) => {
                return globalActions.saveCourseExtensionSuccess({
                    courseParticipationId: a.courseParticipationId,
                    newValidUntil: a.newValidUntil,
                });
            }),
            catchError(error => {
                return of(globalActions.saveCourseExtensionFailure(error));
            })
        );
    });

    saveCourseExtensionSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(globalActions.saveCourseExtensionSuccess),
                tap(a => {
                    this.router.navigate([
                        a.courseParticipationId,
                        'bookextension',
                        'success',
                    ]);
                })
            );
        },
        { dispatch: false }
    );

    saveCourseExtensionFailure$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(globalActions.saveCourseExtensionFailure),
                tap(error => {
                    this.store.dispatch(
                        errodDescribtionActions.setErrorDescription({
                            errorDescription:
                                'Verlängerung des Kurses fehlgeschlagen!',
                        })
                    );
                })
            );
        },
        { dispatch: false }
    );

    checkCourseStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(globalActions.checkCourseStatus),
            withLatestFrom(this.store.select(selectCourseParticipationId)),
            switchMap(([, cpId]) =>
                this.http.get<CourseParticipationStatusResponse>(
                    environment.apiHost + `/course-participation/${cpId}/status`
                )
            ),
            map(response =>
                globalActions.checkCourseStatusSuccess({
                    status: response.course.status,
                })
            ),
            catchError(error =>
                of(globalActions.checkCourseStatusFailure(error))
            )
        )
    );

    migrateCourse$ = createEffect(() =>
        this.actions$.pipe(
            ofType(globalActions.migrateCourse),
            withLatestFrom(
                this.store.select(selectCourseParticipationId),
                this.store.select(selectModuleInfo)
            ),
            switchMap(([, cpId, moduleInfo]) =>
                this.http
                    .post<
                        CourseParticipation & {
                            bookedCourse: Course & {
                                modules: LearningModuleDocumentInfo[];
                                repetitorium: { unlocked: boolean } | undefined;
                            };
                        }
                    >(
                        environment.apiHost +
                            `/course-participation/${cpId}/migrate`,
                        {}
                    )
                    .pipe(
                        map(({ id, bookedCourse }) =>
                            globalActions.migrateCourseSuccess({
                                courseParticipationId: id,
                                moduleId: bookedCourse.modules.find(
                                    ({
                                        sequentialNumber: newSequentialNumber,
                                    }) =>
                                        newSequentialNumber ===
                                        moduleInfo?.sequentialNumber
                                )?.id,
                            })
                        ),
                        catchError(error => {
                            console.error(error);
                            return of(globalActions.migrateCourseFailure(error))
                        })
                    )
            )
        )
    );

    navigateOnMigrationSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(globalActions.migrateCourseSuccess),
                tap(async ({ courseParticipationId, moduleId }) => {
                    this.store.dispatch(loadCourse());
                    if (moduleId) {
                        await this.router.navigate([
                            courseParticipationId,
                            'learning',
                            'moduleOverview',
                            moduleId,
                        ]);
                    } else {
                        await this.router.navigate([
                            courseParticipationId,
                            'learning',
                            'courseOverview',
                        ]);
                    }
                }),
                // takeUntil(this.store.select(select))
            ),
        { dispatch: false }
    );

    navigateOnMigrationFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(globalActions.migrateCourseFailure),
                tap(async () => {
                    this.toasterService.show(
                        'Beim Migrieren Ihres Kurses ist ein Fehler aufgetreten!',
                        ToastType.error
                    );
                    await this.router.navigate(['/']);
                })
            ),
        { dispatch: false }
    );
}
